import { Icon, Page, Sidebar } from '@elipssolution/harfang';
import { mdiAccount, mdiCog, mdiFileSign, mdiLifebuoy, mdiLogout } from '@mdi/js';
import { Stack } from '@mui/material';
import getConfig from 'next/config';
import Image from 'next/image';
import { signOut } from 'next-auth/react';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import modulesConfig from '../../../modules/modules';
import { CustomerFileType } from '../../../types/customerFile';
import { NavigationItemType } from '../../../types/module';
import { PermissionEnum } from '../../../types/permission';
import { ModuleCodeEnum } from '../../../types/widget';
import { renderFullName } from '../../../utils/renderFullName';
import useAlert from '../../hooks/useAlert';
import useCustomRouter from '../../hooks/useCustomRouter';
import { useModules } from '../../hooks/useModules';
import SettingsDialog from '../../settings/SettingsDialog';
import { PublicRuntimeConfigType } from '../../types/publicRuntimeConfig';
import { UserTypeEnum } from '../../types/user';
import AlertBanner from '../AlertBanner';
import SidebarCustomerFileSelector from '../customerFileSelector/SidebarCustomerFileSelector';
import ImpersonateBanner from '../ImpersonateBanner';
import { useLogo } from '../LogoProvider';
import Profile from '../profile/Profile';
import { useSession } from '../session/SessionContext';
import SidebarBody from '../SidebarBody';
import Support from '../support/Support';

type DesktopLayoutContentProps = {
	children: ReactNode;
	onNavigation: ({ route, subItems }: Pick<NavigationItemType, 'route' | 'subItems'>) => Promise<void>;
};

const DesktopLayoutContent = ({ children, onNavigation }: DesktopLayoutContentProps) => {
	const { publicRuntimeConfig } = getConfig() as PublicRuntimeConfigType;
	const { isBrochureHidden } = publicRuntimeConfig;
	const {
		impersonatedUser,
		user: { lastName, firstName, type, hasSettingsAccess },
		checkPermission,
	} = useSession();
	const { query, pathname, replace } = useCustomRouter();

	const { alert } = useAlert();
	const { content: alertContent, isActive: isAlertActive } = alert ?? {};

	const pathnameArray = pathname.split('/').slice(1);
	const [moduleCode] = pathnameArray;

	const { dossier: queryCustomerFileCode } = query || {};

	const { logo, logoCollapsed } = useLogo();

	const modules = useModules();
	const brochureModule = modulesConfig.find(({ code }) => code === ModuleCodeEnum.BROCHURE);

	const [logoUrl, setLogoUrl] = useState<string>();
	const [logoCollapsedUrl, setLogoCollapsedUrl] = useState<string>();
	const [isSupportDialogOpen, setIsSupportDialogOpen] = useState(false);
	const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);

	const isBrochureModuleAccessible = useMemo(
		() => type === UserTypeEnum.INTERNAL && isBrochureHidden !== 'true',
		[isBrochureHidden, type],
	);

	const sidebarBodyModules = useMemo(() => {
		if (modules.length === 0 && isBrochureModuleAccessible && !!brochureModule) {
			return [brochureModule];
		}

		if (
			modules.length > 0 &&
			isBrochureModuleAccessible &&
			!!brochureModule &&
			!modules.some(({ code }) => code === ModuleCodeEnum.BROCHURE)
		) {
			return [...modules, brochureModule];
		}

		return modules;
	}, [brochureModule, isBrochureModuleAccessible, modules]);

	const openSettings = useCallback(() => {
		replace(
			{ query: { ...query, settings: 'true' } },
			`${queryCustomerFileCode ? `?dossier=${queryCustomerFileCode as CustomerFileType['code']}` : ' '}`,
		).catch((error) => {
			throw error;
		});
	}, [query, queryCustomerFileCode, replace]);

	const openSupportDialog = () => setIsSupportDialogOpen(true);

	const closeSupportDialog = () => setIsSupportDialogOpen(false);

	const openProfileDialog = () => setIsProfileDialogOpen(true);

	const closeProfileDialog = () => setIsProfileDialogOpen(false);

	const navigateToBrochure = () => {
		if (
			!brochureModule ||
			![
				PermissionEnum.BROCHURE_GENERAL,
				PermissionEnum.BROCHURE_INVOICING,
				PermissionEnum.BROCHURE_LEGAL,
				PermissionEnum.BROCHURE_STATISTICS,
			].some((permissionCode) => checkPermission(permissionCode))
		) {
			window.open('https://plaquette.afsolutions.fr', '_blank');

			return;
		}

		onNavigation(brochureModule).catch((error) => {
			throw error;
		});
	};

	const logout = () => {
		signOut({ callbackUrl: '/' }).catch((error) => {
			throw error;
		});
	};

	const profileMenuItems = [
		...(!impersonatedUser
			? [
					{
						id: 'profile',
						label: 'Profil',
						icon: <Icon path={mdiAccount} />,
						onClick: openProfileDialog,
					},
			  ]
			: []),
		{
			id: 'logout',
			label: 'Se déconnecter',
			icon: <Icon path={mdiLogout} />,
			onClick: logout,
		},
	];

	useEffect(
		() =>
			setLogoUrl((prev) => {
				if (prev) {
					URL.revokeObjectURL(prev);
				}

				return logo && URL.createObjectURL(logo);
			}),
		[logo],
	);

	useEffect(
		() =>
			setLogoCollapsedUrl((prev) => {
				if (prev) {
					URL.revokeObjectURL(prev);
				}

				return logoCollapsed && URL.createObjectURL(logoCollapsed);
			}),
		[logoCollapsed],
	);

	return (
		<>
			{impersonatedUser && <ImpersonateBanner />}
			{isAlertActive && alertContent && <AlertBanner content={alertContent} />}

			<Stack flex={1} flexDirection="row">
				<Sidebar
					logo={
						logoUrl && (
							<Image
								src={logoUrl}
								loader={({ src }) => src}
								alt="logo"
								width={384}
								height={96}
								objectFit="contain"
								unoptimized
							/>
						)
					}
					logoCollapsed={
						logoCollapsedUrl && (
							<Image
								src={logoCollapsedUrl}
								loader={({ src }) => src}
								alt="logo"
								width={96}
								height={96}
								objectFit="contain"
								unoptimized
							/>
						)
					}
				>
					{moduleCode !== ModuleCodeEnum.BROCHURE && (
						<Sidebar.Header>
							<SidebarCustomerFileSelector />
						</Sidebar.Header>
					)}

					<SidebarBody modules={sidebarBodyModules} />

					<Sidebar.Footer>
						{brochureModule && isBrochureModuleAccessible && moduleCode !== ModuleCodeEnum.BROCHURE && (
							<Sidebar.Item icon={<Icon path={mdiFileSign} />} onClick={navigateToBrochure} label="Process Plaquette" />
						)}
						<Sidebar.Item icon={<Icon path={mdiLifebuoy} />} onClick={openSupportDialog} label="Support" />

						{hasSettingsAccess && (
							<Sidebar.Item icon={<Icon path={mdiCog} />} onClick={openSettings} label="Paramétrage" />
						)}

						<Sidebar.ProfileItem menuItems={profileMenuItems}>
							{renderFullName({ firstName, lastName })}
						</Sidebar.ProfileItem>
					</Sidebar.Footer>
				</Sidebar>

				<Page key={pathname} disablePadding={pathname === '/'}>
					{children}
				</Page>
			</Stack>

			{hasSettingsAccess && <SettingsDialog />}

			<Support isDialogOpen={isSupportDialogOpen} onDialogClose={closeSupportDialog} />

			<Profile open={isProfileDialogOpen} onClose={closeProfileDialog} />
		</>
	);
};

export default DesktopLayoutContent;
